import React from 'react';
import { ArrowDown, Database, Cpu, BarChart3, Shield, Settings, Globe, Zap, MemoryStick, Timer, CheckCircle2, Award, Network, Wifi, Radio } from 'lucide-react';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

const Architecture = () => {
  const [headerRef, headerVisible] = useScrollAnimation({ threshold: 0.3 });
  const [diagramRef, diagramVisible] = useScrollAnimation({ threshold: 0.2 });
  const [protocolRef, protocolVisible] = useScrollAnimation({ threshold: 0.2 });
  const [transportRef, transportVisible] = useScrollAnimation({ threshold: 0.2 });
  const [techRef, techVisible] = useScrollAnimation({ threshold: 0.2 });

  const protocols = [
    { name: 'SBE', desc: 'CME, B3, Binance Futures', color: 'text-blue-400' },
    { name: 'FIX', desc: 'NSE, BSE, LSE, Euronext', color: 'text-purple-400' },
    { name: 'ITCH/OUCH', desc: 'NASDAQ, Nordic exchanges', color: 'text-cyan-400' },
    { name: 'FAST', desc: 'MOEX, commodity exchanges', color: 'text-orange-400' },
    { name: 'WebSocket', desc: 'Crypto exchanges', color: 'text-green-400' },
  ];

  // Network Transport Tiers - from CONFIGURATION_REFERENCE.md
  const transportTiers = [
    {
      icon: Wifi,
      name: 'TCP',
      tier: 'Standard',
      latency: '~10-50μs',
      desc: 'Traditional socket I/O, works everywhere',
      features: ['Zero configuration', 'Universal compatibility', 'Standard TCP/IP stack'],
      borderClass: 'border-blue-500/30 hover:border-blue-500/50',
      bgClass: 'bg-blue-500/10',
      iconClass: 'text-blue-400',
      tierBgClass: 'bg-blue-500/20',
      tierTextClass: 'text-blue-400',
    },
    {
      icon: Network,
      name: 'DPDK',
      tier: 'Kernel Bypass',
      latency: '~1-5μs',
      desc: 'Intel DPDK for kernel bypass networking',
      features: ['10x lower latency', 'Userspace networking', 'Requires DPDK setup'],
      borderClass: 'border-purple-500/30 hover:border-purple-500/50',
      bgClass: 'bg-purple-500/10',
      iconClass: 'text-purple-400',
      tierBgClass: 'bg-purple-500/20',
      tierTextClass: 'text-purple-400',
    },
    {
      icon: Radio,
      name: 'RDMA',
      tier: 'Ultra-Low Latency',
      latency: '<1μs',
      desc: 'Remote Direct Memory Access for ultimate speed',
      features: ['Zero-copy transfers', 'Bypass CPU entirely', 'Mellanox InfiniBand'],
      borderClass: 'border-cyan-500/30 hover:border-cyan-500/50',
      bgClass: 'bg-cyan-500/10',
      iconClass: 'text-cyan-400',
      tierBgClass: 'bg-cyan-500/20',
      tierTextClass: 'text-cyan-400',
      recommended: true,
    },
  ];

  const techDetails = [
    { icon: Cpu, title: 'ASTRA Native Compilation', desc: 'Proprietary LLVM-based pipeline' },
    { icon: MemoryStick, title: 'Modified Disruptor', desc: 'Proprietary ring buffer optimizations' },
    { icon: Zap, title: 'Hardware Optimization', desc: 'CPU-aware data structures' },
    { icon: Timer, title: 'Zero-Allocation', desc: 'No heap allocs in hot path' },
    { icon: CheckCircle2, title: 'Lock-Free Sync', desc: 'Proprietary concurrency model' },
    { icon: Award, title: 'Path Optimization', desc: '11 months of R&D tuning' },
  ];

  return (
    <section id="architecture" className="py-24">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div
          ref={headerRef}
          className={`text-center max-w-3xl mx-auto mb-16 transition-all duration-700 ${
            headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-yellow-500/10 border border-yellow-500/20 mb-6">
            <Settings className="w-4 h-4 text-yellow-400" />
            <span className="text-sm font-medium text-yellow-400">Technical Architecture</span>
          </div>
          <h2 className="text-4xl sm:text-5xl font-extrabold text-white mb-4 tracking-tight">
            Target: <span className="text-gradient">BlueField-3 DPU</span> Trading Architecture
          </h2>
          <p className="text-lg text-muted-foreground">
            ASTRA designed for NVIDIA BlueField-3 DPU's ARM cores with AMD Alveo UL3422 FPGA for protocol offload.
            Target: 150-250ns tick-to-trade with full quant strategy execution.
          </p>
        </div>

        {/* Architecture Diagram */}
        <div
          ref={diagramRef}
          className={`max-w-4xl mx-auto mb-16 transition-all duration-700 ${
            diagramVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          {/* Production Architecture Overview */}
          <div className="mb-8">
            <div className="text-center mb-4">
              <span className="text-xs font-medium text-yellow-400 uppercase tracking-wider">
                Production Co-Location Stack
              </span>
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="p-4 rounded-xl bg-gradient-to-br from-orange-500/10 to-red-500/10 border border-orange-500/30 text-center">
                <Database className="w-8 h-8 text-orange-400 mx-auto mb-2" />
                <div className="font-medium text-white">AMD Alveo UL3422</div>
                <div className="text-xs text-muted-foreground mt-1">FPGA protocol decode/encode</div>
              </div>
              <div className="p-4 rounded-xl bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-500/30 text-center">
                <Cpu className="w-8 h-8 text-purple-400 mx-auto mb-2" />
                <div className="font-medium text-white">BlueField-3 DPU</div>
                <div className="text-xs text-muted-foreground mt-1">ASTRA runs on ARM cores</div>
              </div>
              <div className="p-4 rounded-xl bg-gradient-to-br from-cyan-500/10 to-blue-500/10 border border-cyan-500/30 text-center">
                <Zap className="w-8 h-8 text-cyan-400 mx-auto mb-2" />
                <div className="font-medium text-white">AMD EPYC 9V84</div>
                <div className="text-xs text-muted-foreground mt-1">Host CPU (optional)</div>
              </div>
            </div>
          </div>

          {/* Arrow */}
          <div className="flex justify-center my-6">
            <div className="flex flex-col items-center">
              <ArrowDown className="w-6 h-6 text-yellow-400 animate-bounce" />
              <span className="text-xs text-yellow-400 mt-2">Target: 150-250ns Tick-to-Trade</span>
            </div>
          </div>

          {/* ASTRA Engine */}
          <div className="p-8 rounded-2xl bg-gradient-to-r from-blue-500/10 to-cyan-500/10 border border-blue-500/30">
            <div className="text-center mb-6">
              <span className="px-3 py-1 text-xs font-medium bg-cyan-500/20 text-cyan-400 rounded-full">
                ASTRA EXPRESS LANE ENGINE
              </span>
            </div>

            <div className="grid md:grid-cols-4 gap-4">
              <div className="p-4 rounded-xl bg-background/50 border border-border text-center">
                <div className="font-medium text-white text-sm">Protocol Adapters</div>
                <div className="text-xs text-muted-foreground mt-1">6 native protocols</div>
              </div>
              <div className="p-4 rounded-xl bg-background/50 border border-border text-center">
                <div className="font-medium text-white text-sm">Decision Engine</div>
                <div className="text-xs text-cyan-400 mt-1">~2ns amortized</div>
              </div>
              <div className="p-4 rounded-xl bg-background/50 border border-border text-center">
                <div className="font-medium text-white text-sm">Risk Framework</div>
                <div className="text-xs text-muted-foreground mt-1">Configurable limits</div>
              </div>
              <div className="p-4 rounded-xl bg-background/50 border border-border text-center">
                <div className="font-medium text-white text-sm">Position Tracking</div>
                <div className="text-xs text-muted-foreground mt-1">Real-time P&L</div>
              </div>
            </div>

            <div className="flex justify-center items-center gap-8 mt-6 pt-6 border-t border-border/50">
              <div className="flex items-center gap-2">
                <Cpu className="w-4 h-4 text-cyan-400" />
                <span className="text-xs text-muted-foreground">Codon-Native</span>
              </div>
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-green-400" />
                <span className="text-xs text-muted-foreground">On-Premise Only</span>
              </div>
            </div>

            {/* Technology Note */}
            <div className="mt-6 pt-6 border-t border-border/50 text-center">
              <p className="text-xs text-muted-foreground">
                Powered by MIT's <span className="text-cyan-400">Codon compiler</span> — Python syntax compiled to LLVM native machine code.
                <span className="text-yellow-400"> First known</span> HFT implementation using this breakthrough technology.
                11 months of R&D delivering <span className="text-green-400">950M+ decisions/sec</span> on commodity hardware.
              </p>
            </div>
          </div>
        </div>

        {/* Protocol Support - Merged from GlobalMarkets */}
        <div
          ref={protocolRef}
          className={`max-w-4xl mx-auto mb-16 transition-all duration-700 ${
            protocolVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="p-6 rounded-2xl bg-card/50 border border-border">
            <div className="flex items-center justify-center gap-2 mb-6">
              <Globe className="w-5 h-5 text-blue-400" />
              <h3 className="text-lg font-bold text-white">Native Protocol Support</h3>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {protocols.map((protocol, index) => (
                <div key={index} className="text-center p-3 rounded-xl bg-background/50 border border-border">
                  <div className={`text-lg font-bold ${protocol.color} mb-1`}>{protocol.name}</div>
                  <div className="text-xs text-muted-foreground">{protocol.desc}</div>
                </div>
              ))}
            </div>
            <p className="text-center text-xs text-muted-foreground mt-4">
              Works with any exchange that supports these protocols. Equities, futures, options, crypto.
            </p>
          </div>
        </div>

        {/* Network Transport Tiers - NEW */}
        <div
          ref={transportRef}
          className={`max-w-4xl mx-auto mb-16 transition-all duration-700 ${
            transportVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="text-center mb-6">
            <h3 className="text-lg font-bold text-white mb-2">Choose Your Network Stack</h3>
            <p className="text-sm text-muted-foreground">
              Three transport tiers to match your latency requirements and infrastructure
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            {transportTiers.map((tier, index) => (
              <div
                key={index}
                className={`relative p-5 rounded-xl bg-card/50 border ${tier.borderClass} transition-all duration-300 hover:scale-[1.02]`}
              >
                {tier.recommended && (
                  <div className="absolute -top-2.5 left-1/2 -translate-x-1/2">
                    <span className="px-2 py-0.5 text-[10px] font-bold bg-cyan-500 text-white rounded-full">
                      RECOMMENDED
                    </span>
                  </div>
                )}
                <div className="flex items-center justify-between mb-3">
                  <div className={`w-10 h-10 rounded-lg ${tier.bgClass} flex items-center justify-center`}>
                    <tier.icon className={`w-5 h-5 ${tier.iconClass}`} />
                  </div>
                  <span className={`px-2 py-1 rounded-full text-[10px] font-medium ${tier.tierBgClass} ${tier.tierTextClass}`}>
                    {tier.tier}
                  </span>
                </div>
                <h4 className="text-base font-bold text-white mb-1">{tier.name}</h4>
                <div className="text-lg font-bold text-cyan-400 mb-2">{tier.latency}</div>
                <p className="text-xs text-muted-foreground mb-3">{tier.desc}</p>
                <ul className="space-y-1">
                  {tier.features.map((feature, i) => (
                    <li key={i} className="text-xs text-muted-foreground flex items-center gap-1.5">
                      <CheckCircle2 className="w-3 h-3 text-green-400 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <p className="text-center text-xs text-muted-foreground mt-4">
            All tiers use the same ~2ns amortized decision engine. Network I/O latency is additional.
          </p>
        </div>

        {/* How We Achieve Sub-2ns - Merged from Performance */}
        <div
          ref={techRef}
          className={`max-w-4xl mx-auto mb-12 transition-all duration-700 ${
            techVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <h3 className="text-lg font-bold text-white text-center mb-6">How We Achieve ~2ns Amortized Decisions</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {techDetails.map((item, index) => (
              <div
                key={index}
                className="p-4 rounded-xl bg-card/50 border border-border hover:border-cyan-500/30 transition-colors"
              >
                <item.icon className="w-5 h-5 text-cyan-400 mb-2" />
                <h4 className="text-sm font-semibold text-white mb-1">{item.title}</h4>
                <p className="text-xs text-muted-foreground">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Config Example */}
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-4">
            <span className="text-sm font-medium text-muted-foreground">Configuration Example</span>
          </div>
          <div className="bg-card/50 backdrop-blur-xl rounded-2xl border border-border overflow-hidden">
            <div className="flex items-center gap-2 px-4 py-3 border-b border-border">
              <div className="w-3 h-3 rounded-full bg-red-500" />
              <div className="w-3 h-3 rounded-full bg-yellow-500" />
              <div className="w-3 h-3 rounded-full bg-green-500" />
              <span className="ml-2 text-xs text-muted-foreground font-mono">astra_config.yaml</span>
            </div>
            <div className="p-6 font-mono text-sm">
              <pre className="text-muted-foreground">
                <code>
                  <span className="text-purple-400">engine:</span>{'\n'}
                  {'  '}<span className="text-blue-400">mode:</span> <span className="text-green-400">paper</span>  <span className="text-gray-500"># or 'live'</span>{'\n'}
                  {'  '}<span className="text-blue-400">symbols:</span> <span className="text-green-400">[AAPL, MSFT, GOOGL]</span>{'\n'}
                  {'  '}<span className="text-blue-400">warmup_ticks:</span> <span className="text-cyan-400">50</span>{'\n'}
                  {'\n'}
                  <span className="text-purple-400">protocol:</span>{'\n'}
                  {'  '}<span className="text-blue-400">type:</span> <span className="text-green-400">fix</span>{'\n'}
                  {'  '}<span className="text-blue-400">venue:</span> <span className="text-green-400">nasdaq</span>{'\n'}
                  {'\n'}
                  <span className="text-purple-400">modules:</span>{'\n'}
                  {'  '}<span className="text-blue-400">risk:</span>{'\n'}
                  {'    '}<span className="text-blue-400">enabled:</span> <span className="text-cyan-400">true</span>{'\n'}
                  {'    '}<span className="text-blue-400">max_daily_loss:</span> <span className="text-cyan-400">50000</span>{'\n'}
                  {'  '}<span className="text-blue-400">logging:</span>{'\n'}
                  {'    '}<span className="text-blue-400">mode:</span> <span className="text-green-400">external</span>  <span className="text-gray-500"># splunk/elk</span>
                </code>
              </pre>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Architecture;
